import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { DebouncedInput, DebouncedTextarea } from "@/components/DebouncedField";
import { toast } from "sonner";

export default function TemplateDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const [template, setTemplate] = useState(null);
  const [elementConfigText, setElementConfigText] = useState("");
  const [jsonError, setJsonError] = useState("");

  const load = async () => {
    const { data } = await api.get(`/templates/${id}`);
    setTemplate(data);
    setElementConfigText(JSON.stringify(data.elementConfig || {}, null, 2));
  };

  useEffect(() => { load(); }, [id]);

  const save = async (patch) => {
    const merged = { ...template, ...patch };
    setTemplate(merged);
    try {
      await api.put(`/templates/${id}`, merged);
    } catch (e) { toast.error(t("templateDetail.saveFailed")); }
  };

  const saveElementConfig = async () => {
    try {
      const parsed = JSON.parse(elementConfigText);
      setJsonError("");
      await save({ elementConfig: parsed, version: (template.version || 1) + 1 });
      toast.success(t("templateDetail.elementConfigSaved"));
    } catch (e) {
      setJsonError(t("templateDetail.invalidJson") + e.message);
    }
  };

  if (!template) return <div className="p-10 text-sm text-slate-400">{t("common.loading")}</div>;

  return (
    <div className="mx-auto max-w-4xl px-8 py-10" data-testid="template-detail-page">
      <button onClick={() => navigate("/templates")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800" data-testid="back-to-templates-button">
        <ArrowLeft className="h-4 w-4" /> {t("templateDetail.back")}
      </button>
      {!isAdmin && (
        <div className="mt-4 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-xs text-amber-700" data-testid="template-readonly-banner">
          <Lock className="h-3.5 w-3.5" /> {t("common.readOnly")} · {t("common.adminOnly")}
        </div>
      )}
      <fieldset disabled={!isAdmin} className="m-0 min-w-0 border-0 p-0">
      <div className="mt-4 flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-700 text-slate-900">{template.templateName}</h1>
          <p className="font-mono-bhf text-xs text-slate-400">{template.templateCode} · v{template.version}</p>
        </div>
        <Select value={template.status} onValueChange={(v) => save({ status: v })}>
          <SelectTrigger className="w-32" data-testid="template-status-select"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="Active">{t("products.active")}</SelectItem><SelectItem value="Inactive">{t("products.inactive")}</SelectItem></SelectContent>
        </Select>
      </div>

      <div className="mt-8 bhf-card p-6">
        <h2 className="font-display text-sm font-700 text-slate-800">{t("templateDetail.pageSetup")}</h2>
        <div className="mt-4 grid grid-cols-3 gap-4">
          <div><Label>{t("templateDetail.width")}</Label><DebouncedInput key={`width-${id}`} type="number" className="mt-1" initialValue={template.widthMM} onCommit={(v) => save({ widthMM: Number(v) })} data-testid="template-width-input" /></div>
          <div><Label>{t("templateDetail.height")}</Label><DebouncedInput key={`height-${id}`} type="number" className="mt-1" initialValue={template.heightMM} onCommit={(v) => save({ heightMM: Number(v) })} data-testid="template-height-input" /></div>
          <div><Label>{t("templateDetail.bleed")}</Label><DebouncedInput key={`bleed-${id}`} type="number" className="mt-1" initialValue={template.bleedMM} onCommit={(v) => save({ bleedMM: Number(v) })} data-testid="template-bleed-input" /></div>
        </div>
      </div>

      <div className="mt-6 bhf-card p-6">
        <h2 className="font-display text-sm font-700 text-slate-800">{t("templateDetail.templateSvg")}</h2>
        <DebouncedTextarea key={`svg-${id}`} className="mt-3 font-mono-bhf text-xs" rows={5} placeholder={t("templateDetail.svgPlaceholder")} initialValue={template.templateSVG || ""} onCommit={(v) => save({ templateSVG: v })} data-testid="template-svg-input" />
      </div>

      <div className="mt-6 bhf-card p-6">
        <h2 className="font-display text-sm font-700 text-slate-800">{t("templateDetail.elementConfig")}</h2>
        <p className="mt-1 text-xs text-slate-500">{t("templateDetail.elementConfigHint")}</p>
        <Textarea className="mt-3 font-mono-bhf text-xs" rows={16} value={elementConfigText} onChange={(e) => setElementConfigText(e.target.value)} data-testid="template-element-config-input" />
        {jsonError && <p className="mt-2 text-xs text-red-600" data-testid="element-config-error">{jsonError}</p>}
        <Button className="bhf-btn-primary mt-3 border-0 text-white" onClick={saveElementConfig} data-testid="save-element-config-button">{t("templateDetail.saveElementConfig")}</Button>
      </div>
      </fieldset>
    </div>
  );
}
